/* eslint-disable max-lines */
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { Box, Button, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DialogFullscreen } from '@styles/reusableComponents.styled';
import { useEffect, useMemo, useState } from 'react';
import TabPanel from '@components/TabPanel';
import { setSetupLanguageChanges, setSetupLanguageDialog } from '@store/reducers/project.reducer';
import {
  CONFIRM_ACTION,
  EMPTY_STRING,
  GENERAL_SETUP_LANGUAGE_OPTIONS,
  PROJECT_ROLES,
  PROJECT_SETUP_LANGUAGE_OPTIONS,
  SETUP_LANGUAGE_TABS,
  translationDialogDefaultState,
  ZERO,
} from '@constants';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  GeneralTranslationLanguages,
  Nullable,
  ProjectTranslationLanguages,
  TranslationContentMutation,
  TranslationSetupForm,
} from '@customTypes';
import {
  useGetGeneralOverviewQuery,
  useGetProjectOverviewQuery,
  useSaveGeneralTranslationsMutation,
  useSaveProjectTranslationsMutation,
} from '@services/api/translation.api';
import { useGetUserPermissionsQuery } from '@services/api/homepage.api';

import styles from './TranslationSetupDialog.module.scss';
import TranslationSetupTab from './TranslationSetupTab';
import { LanguageControls } from './LanguageControls';
import { ConfirmationDialog } from './ConfirmationDialog';
import { getFormFieldKey } from './TranslationSetupDialog.helpers';

export const TranslationSetupDialog = () => {
  const {
    setupLanguageDialog: { isOpen, changedValues },
    projects,
    selectedProject,
    userRole,
  } = useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();
  const { isParticipantMode } = useAppSelector((state) => state.user);

  const { getTranslation } = useCustomTranslation();
  const [confirmAction, setConfirmAction] = useState<Nullable<CONFIRM_ACTION>>(null);
  const [languages, setLanguages] = useState<GeneralTranslationLanguages[] | ProjectTranslationLanguages | string>(
    EMPTY_STRING,
  );
  const [currentTab, setCurrentTab] = useState<number>(ZERO);

  const methods = useForm<TranslationSetupForm>({
    mode: 'onChange',
    defaultValues: {
      language: EMPTY_STRING,
      projectAnalysisActiveTab: ZERO,
      projectTranslationActiveTab: ZERO,
    },
  });

  const { setValue } = methods;

  const { projectAnalysisActiveTab, language } = useWatch(methods);

  const { data: permissions } = useGetUserPermissionsQuery(undefined, { skip: isParticipantMode });

  const isAdmin = useMemo(() => {
    return permissions?.payload.isPimsAdmin || userRole === PROJECT_ROLES.ADMIN;
  }, [permissions, userRole]);

  const { data: generalOverview } = useGetGeneralOverviewQuery(undefined, {
    skip: currentTab !== +SETUP_LANGUAGE_TABS.GENERAL || !isAdmin,
  });
  const { data: projectOverview } = useGetProjectOverviewQuery(
    { businessId: selectedProject?.selectedBusinessId },
    { skip: !selectedProject?.selectedBusinessId || !isAdmin },
  );

  const [targetSaveGeneral, { isLoading: isGeneralLoading }] = useSaveGeneralTranslationsMutation();
  const [targetSaveProject, { isLoading: isProjectLoading }] = useSaveProjectTranslationsMutation();

  const projectName = useMemo(() => {
    if (selectedProject) {
      return (
        projects.find((project) => project.businessId === selectedProject.selectedBusinessId)?.description ??
        EMPTY_STRING
      );
    }
  }, [projects, selectedProject]);

  const hasChanges = useMemo(() => !!Object.keys(changedValues).length, [changedValues]);

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    if (hasChanges) {
      handleProceedWithConfirmation(CONFIRM_ACTION.TAB_CHANGE);
    } else {
      setCurrentTab(value);
      setValue('projectTranslationActiveTab', value);
      setValue('language', EMPTY_STRING);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      handleProceedWithConfirmation(CONFIRM_ACTION.TAB_CHANGE);
    } else {
      setCurrentTab(ZERO);
      dispatch(setSetupLanguageDialog(translationDialogDefaultState));
    }
  };

  const handleCancel = () => {
    dispatch(setSetupLanguageDialog(translationDialogDefaultState));
  };

  const handleReset = () => {
    Object.keys(changedValues).forEach((key) => {
      setValue(getFormFieldKey(key, projectAnalysisActiveTab!), changedValues[key].oldValue);
    });
    dispatch(setSetupLanguageChanges({}));
  };

  const handleSave = async () => {
    if (!hasChanges && !language) return;
    const changedData = Object.keys(changedValues).map((key) => ({
      textKey: key,
      translatedText: changedValues[key].newValue,
    })) as TranslationContentMutation;
    try {
      if (currentTab === +SETUP_LANGUAGE_TABS.GENERAL) {
        await targetSaveGeneral({
          languageId: language,
          languagePool: GENERAL_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab!],
          body: changedData,
        });
      } else if (currentTab === +SETUP_LANGUAGE_TABS.PROJECT) {
        await targetSaveProject({
          languageId: language,
          businessId: selectedProject?.selectedBusinessId,
          languagePool: PROJECT_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab!],
          body: changedData,
        });
      }
      dispatch(setSetupLanguageChanges({}));
    } catch (error) {
      console.error(error);
    }
  };

  const handleProceedWithConfirmation = (action: CONFIRM_ACTION) => {
    setConfirmAction(action);
  };

  const handleConfirmationCancel = (action?: CONFIRM_ACTION) => {
    if (action === CONFIRM_ACTION.TAB_CHANGE) {
      handleReset();
    }
    setConfirmAction(null);
  };

  const handleProceed = async (action: CONFIRM_ACTION) => {
    if (action === CONFIRM_ACTION.RESET) {
      handleReset();
    } else {
      await handleSave();
    }
    setConfirmAction(null);
  };

  const languageControlsData = useMemo(() => {
    if (!languages) return null;

    if (currentTab === +SETUP_LANGUAGE_TABS.GENERAL) {
      const generalTranslations = languages as GeneralTranslationLanguages[];

      return generalTranslations[projectAnalysisActiveTab!];
    } else if (currentTab === +SETUP_LANGUAGE_TABS.PROJECT && !Array.isArray(languages)) {
      return languages as ProjectTranslationLanguages;
    }

    return null;
  }, [languages, currentTab, projectAnalysisActiveTab]);

  useEffect(() => {
    if (currentTab === +SETUP_LANGUAGE_TABS.GENERAL) {
      setLanguages(generalOverview?.payload.generalTranslations || EMPTY_STRING);
    } else if (currentTab === +SETUP_LANGUAGE_TABS.PROJECT) {
      setLanguages(projectOverview?.payload.projectTranslation || EMPTY_STRING);
    }
  }, [generalOverview, projectOverview, currentTab]);

  return (
    <DialogFullscreen open={isOpen} keepMounted={false} className={styles.addLanguageDialogContainer} isFullscreen>
      <DialogTitle className={styles.dialogTitle}>
        <Box>{getTranslation('LangSel.Input', 'addLanguage.modalTitle')}</Box>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon color="action" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.content}>
        <FormProvider {...methods}>
          {projectName && (
            <Box>
              <Tabs value={currentTab} onChange={handleTabChange} className={styles.tabs}>
                <Tab
                  label={getTranslation(
                    'CvaProj.addLanguage.generalTranslation.title',
                    'addLanguage.generalTranslations.title',
                  )}
                />
                <Tab label={projectName} />
              </Tabs>
            </Box>
          )}
          {languageControlsData && (
            <LanguageControls
              data={languageControlsData}
              hasChanges={hasChanges}
              onAddClick={() => handleProceedWithConfirmation(CONFIRM_ACTION.TAB_CHANGE)}
            />
          )}
          <div className={styles.tabsContent}>
            <TabPanel index={SETUP_LANGUAGE_TABS.GENERAL} value={currentTab}>
              <TranslationSetupTab
                type={SETUP_LANGUAGE_TABS.GENERAL}
                confirmTabChange={() => handleProceedWithConfirmation(CONFIRM_ACTION.TAB_CHANGE)}
              />
            </TabPanel>
            <TabPanel index={SETUP_LANGUAGE_TABS.PROJECT} value={currentTab}>
              <TranslationSetupTab
                type={SETUP_LANGUAGE_TABS.PROJECT}
                confirmTabChange={() => handleProceedWithConfirmation(CONFIRM_ACTION.TAB_CHANGE)}
              />
            </TabPanel>
          </div>
          <Box className={styles.footer}>
            <Tooltip arrow placement="top" title={getTranslation('CvaProj.ResetTranslations')}>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => handleProceedWithConfirmation(CONFIRM_ACTION.RESET)}
                  disabled={!hasChanges}
                >
                  {getTranslation('Generic.Reset', 'buttons.reset')}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="medium" onClick={handleCancel}>
              {getTranslation('Generic.Cancel', 'buttons.cancel')}
            </Button>
            <Tooltip arrow placement="top" title={getTranslation('CvaProj.TranslationsSaveAll')}>
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => handleProceedWithConfirmation(CONFIRM_ACTION.SAVE)}
                  disabled={!hasChanges}
                >
                  {getTranslation('Generic.Save', 'buttons.save')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </FormProvider>
      </DialogContent>
      <ConfirmationDialog
        action={confirmAction}
        handleClose={(action?: CONFIRM_ACTION) => handleConfirmationCancel(action)}
        handleProceed={(action: CONFIRM_ACTION) => handleProceed(action)}
        isLoading={isProjectLoading || isGeneralLoading}
      />
    </DialogFullscreen>
  );
};
